import React, {useCallback, useEffect, useMemo, useState} from "react";
import {getExpLvls, getSkills, getSkillTypes} from "../../../../service/dataService";
import {getUserSkills} from "../../../../service/userService";
import BasicGrid from "../../../grid/BasicGrid";
import EditSkillModal from "../../../modals/skills/EditSkillModal";
import {Button, Divider, toaster} from "rsuite";
import SuccessNotification from "../../../notifications/SuccessNotification";
import {SKILL_ADD_ERROR, SKILL_ADD_SUCCESS, SKILL_EDIT_ERROR, SKILL_EDIT_SUCCESS} from "../../../../service/constants";
import ErrorNotification from "../../../notifications/ErrorNotification";
import AddSkillModal from "../../../modals/skills/AddSkillModal";

const defaultColumns = [
    {
      key: 'id',
      label: 'id',
    },
    {
        key: 'skillName',
        label: 'Skill Name',
    },
    {
        key: 'skillType',
        label: 'Skill Type',
    },
    {
        key: 'skillExpLevel',
        label: 'Experience Level',
    },
    {
        key: 'yearsExp',
        label: 'Years Experience',
    },
];

const UserEditSkills = (props) => {

    const { selectedUser } = props;

    const [unformattedSkills, setUnformattedSkills] = useState([]);
    const [skills, setSkills] = useState([]);
    const [skillTypes, setSkillTypes] = useState([]);
    const [expLvls, setExpLvls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userSkills, setUserSkills] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedSkill, setSelectedSkill] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    const handleAddClose = useCallback(({result, closeModal}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={SKILL_ADD_SUCCESS} />, { placement: 'topCenter' });
            setLoading(true);
            getUserSkills(selectedUser.id).then((skills) => {
                setUserSkills(skills);
                setLoading(false);
            });
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={SKILL_ADD_ERROR} />, { placement: 'topCenter' });
        }
        if (closeModal) {
            setAddModalOpen(false);
        }
    },[selectedUser]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={SKILL_EDIT_SUCCESS} />, { placement: 'topCenter' });
            setLoading(true);
            getUserSkills(selectedUser.id).then((skills) => {
                setUserSkills(skills);
                setLoading(false);
            });
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={SKILL_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [selectedUser]);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedSkill = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedSkill(rowData);
    },[]);

    useEffect(() => {
        setLoading(true);
        getSkills().then((data) => {
                // format the data into InputPicker format for now
                setSkills(data.map(ele => ({label: ele.skillName, value: ele.skillName})));
                setUnformattedSkills(data);
            }
        );
        getSkillTypes().then((data) => {
                // format the data into InputPicker format for now
                setSkillTypes(data.map(ele => ({label: ele.skillTypeName, value: ele.skillTypeName})));
            }
        );
        getExpLvls().then((data) => {
                // format the data into InputPicker format for now
                setExpLvls(data.map(ele => ({label: ele.expLevelName, value: ele.expLevelName})));
            }
        );
        getUserSkills(selectedUser.id).then((skills) => {
            setUserSkills(skills);
            setLoading(false);
        });
    }, [selectedUser]);

    // TODO revisit this later and see if there are improvements to be made
    // Problem: on user change, that id was not being passed down to modals and updating their state from props.
    // This is apparently an anti pattern - using derived state.
    // Need to pass the id as key to the modals, this will cause React to reccreate the modal
    // https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
    return useMemo(() => {
        return (
            <div className="main-content">
                <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Skill</Button>
                <Divider />
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={userSkills}
                    handleClick={setClickedSkill}
                    exportTitle={`admin-edit-${selectedUser.lastName}-${selectedUser.firstName}-skills`}
                />
                <AddSkillModal
                    key={`add-skill-modal-${selectedUser.id}`}
                    open={addModalOpen}
                    handleClose={handleAddClose}
                    skills={skills}
                    skillTypes={skillTypes}
                    skillExpLevels={expLvls}
                    userId={selectedUser.id}
                    unformattedSkills={unformattedSkills}
                />
                <EditSkillModal
                    key={`edit-skill-modal-${selectedUser.id}`}
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    skill={selectedSkill}
                    skillExpLevels={expLvls}
                />
            </div>
        );
    }, [
        addModalOpen,
        columns,
        editModalOpen,
        expLvls,
        handleAddClose,
        handleEditClose,
        loading,
        selectedSkill,
        setClickedSkill,
        skills,
        skillTypes,
        unformattedSkills,
        selectedUser.id,
        selectedUser.firstName,
        selectedUser.lastName,
        userSkills
    ]);
}

export default UserEditSkills;